import {
    Button,
    type Country3Codes,
    CountryPicker,
    LuneThemeNew,
    LuneThemeProvider,
    Modal,
    Select,
    Text,
} from '@lune-fe/lune-ui-lib'
import AddIcon from '@mui/icons-material/Add'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { Box } from '@mui/material'
import { useState } from 'react'
import ReactDOM from 'react-dom/client'

import TradeLane from 'views/CalculateEmissions/Logistics/TradeLane'
import AutoFillLabel from 'views/CalculateEmissions/RFQ/AutoFillLabel'
import Field from 'views/CalculateEmissions/RFQ/components/Field'
import GroupedShippingMethodPicker from 'views/CalculateEmissions/RFQ/components/GroupedShippingMethodPicker'
import Labels from 'views/CalculateEmissions/RFQ/components/Labels'
import { showAddMapValueModal } from 'views/CalculateEmissions/RFQ/modals/AddMapValuesModal'
import {
    TransportMode,
    transportModePerGroupArray,
} from 'views/CalculateEmissions/RFQ/utils/sharedLogic'
import {
    findBestStringMatchIndex,
    generateInputWithAliases,
} from 'views/CalculateEmissions/RFQ/utils/utils'

interface MapValuesProps<T> {
    label: string
    outputValues: readonly T[]
    formatOutput: (value: T) => string
    mappings: [string, T][]
    isTradelane?: boolean
    isTransportMode?: boolean
    isCountryCode?: boolean
    autoFill?: boolean
}

interface MapValuesModalProps<T> extends MapValuesProps<T> {
    modalOpen: boolean
    onModalClose: (selection?: [string, T][]) => void
}

const LocalSelect = ({
    items,
    value,
    onChange,
    autoFill,
}: {
    items: { label: string; value: number }[]
    value: number | undefined
    onChange: (e: number) => void
    autoFill?: { state: boolean; userAction: () => void }
}) => {
    const [localValue, setLocalValue] = useState<number | undefined>(value)
    return (
        <Select
            sx={{
                width: '100%',
                zIndex: 0,
                ...(autoFill?.state && {
                    backgroundColor: 'Blue50',
                    '&:hover': {
                        backgroundColor: 'Blue100',
                    },
                }),
            }}
            value={localValue}
            onChange={(val) => {
                // converting toString first because of value 0 which is valid here
                if (val?.toString()) {
                    setLocalValue(val)
                    onChange(val)
                }
            }}
            onClose={() => autoFill?.userAction()}
            items={items}
        />
    )
}

const LocalTradeLane = ({
    value,
    onChange,
    autoFill,
}: {
    value: string | undefined
    onChange: (e: string) => void
    autoFill?: { state: boolean; userAction: () => void }
}) => {
    const [localValue, setLocalValue] = useState<string | undefined>(
        value === 'Other' ? 'disaggregated_other' : value,
    )
    return (
        <Box
            sx={{
                width: '100%',
                '> div': {
                    width: '100%',
                },
            }}
        >
            <TradeLane
                value={localValue}
                onChange={(tradeLane) => {
                    if (tradeLane) {
                        setLocalValue(tradeLane)
                        onChange(tradeLane)
                    }
                }}
                onClose={() => autoFill?.userAction()}
                sx={{
                    ...(autoFill?.state && {
                        '.MuiInputBase-root': {
                            backgroundColor: 'Blue50',
                            '&:hover': {
                                backgroundColor: 'Blue100',
                            },
                        },
                    }),
                }}
            />
        </Box>
    )
}

const transportModeSet = new Set<string>(Object.values(transportModePerGroupArray).flat())

const isTransportModeType = (val: unknown): val is TransportMode => {
    return typeof val === 'string' && transportModeSet.has(val)
}

const MapValuesModal = <T,>({
    label,
    formatOutput,
    outputValues,
    mappings,
    modalOpen,
    onModalClose,
    isTradelane,
    isTransportMode,
    isCountryCode,
    autoFill,
}: MapValuesModalProps<T>) => {
    const [localMappings, setLocalMappings] = useState<[string, T][]>(mappings)
    const [localAutoFillMappings, setLocalAutoFillMappings] = useState<boolean[]>(
        Array(mappings.length).fill(autoFill),
    )
    const handleChange = (index: number, value: number) => {
        const newValueIndex = value
        const newMappings = [...localMappings]
        newMappings[index] = [localMappings.map(([i, _o]) => i)[index], outputValues[newValueIndex]]
        setLocalMappings(newMappings)
    }

    // TODO all these handle functions do the exact same logic. We are just adapting types
    const handleTradeLaneChange = (index: number, selected: string) => {
        const newMappings = [...localMappings]
        newMappings[index] = [localMappings.map(([i, _o]) => i)[index], selected as T]
        setLocalMappings(newMappings)
    }

    const handleTransportModeChange = (index: number, selected: TransportMode | undefined) => {
        const newMappings = [...localMappings]
        newMappings[index] = [localMappings.map(([i, _o]) => i)[index], selected as T]
        setLocalMappings(newMappings)
    }

    const handleCountryCodeChange = (index: number, value: Country3Codes) => {
        const newMappings = [...localMappings]
        newMappings[index] = [localMappings.map(([i, _o]) => i)[index], value as T]
        setLocalMappings(newMappings)
    }

    const handleAddNewMapping = (selection?: string) => {
        // We want to ignore both empty string and undefined here
        if (
            selection &&
            localMappings.find(([input, _o]) => input.localeCompare(selection) === 0) === undefined
        ) {
            // Find the most appropriate output value based on added mapping value
            const aliases = generateInputWithAliases(selection)
            const bestMatch = findBestStringMatchIndex(aliases, outputValues.map(String))
            const newMappings = [...localMappings]
            newMappings.push([selection, outputValues[bestMatch.index]])
            setLocalMappings(newMappings)
        }
    }

    return (
        <LuneThemeProvider theme={LuneThemeNew}>
            <Modal
                maxWidth={'md'}
                fullWidth
                open={modalOpen}
                onClose={() => onModalClose()}
                header={
                    <Text variant={'h5'}>
                        Map values for column:{' '}
                        <span style={{ textTransform: 'capitalize' }}>{label}</span>
                    </Text>
                }
                subheader={
                    <Text variant={'body1'}>
                        This is the mapping of the unique values in your spreadsheet to Lune’s
                        values
                    </Text>
                }
                actions={
                    <>
                        <Button variant={'outlined'} wide onClick={() => onModalClose()}>
                            Cancel
                        </Button>
                        <Button
                            variant={'contained'}
                            wide
                            onClick={() => onModalClose(localMappings)}
                        >
                            Map values
                        </Button>
                    </>
                }
            >
                <Box
                    sx={{
                        display: 'flex',
                        width: '100%',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexDirection: 'column',
                        gap: 1,
                    }}
                >
                    <Box sx={{ width: '100%', mb: 2 }}>
                        {localAutoFillMappings.find((e) => e) !== undefined && <AutoFillLabel />}
                    </Box>
                    <Labels customLeft={'Your Unique Value'} customRight={'Lune Values'} />
                    {localMappings.map(([input, output], index) => (
                        <Box
                            key={index}
                            sx={{
                                display: 'flex',
                                width: '100%',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flexDirection: 'row',
                            }}
                        >
                            <Box sx={{ width: '45%' }}>
                                <Field label={input} />
                            </Box>
                            <ArrowForwardIcon />
                            <Box sx={{ width: '45%' }}>
                                {isTradelane ? (
                                    <LocalTradeLane
                                        value={output as string}
                                        onChange={(e) => handleTradeLaneChange(index, e)}
                                        autoFill={{
                                            state: localAutoFillMappings[index],
                                            userAction: () =>
                                                setLocalAutoFillMappings((prev) => {
                                                    const newMappings = [...prev]
                                                    newMappings[index] = false
                                                    return newMappings
                                                }),
                                        }}
                                    />
                                ) : isTransportMode ? (
                                    (() => {
                                        if (!isTransportModeType(output)) {
                                            throw new Error(
                                                `Invariant failed: isTransportMode and isTransportModeType disagree for value: ${output}`,
                                            )
                                        }
                                        return (
                                            <GroupedShippingMethodPicker
                                                value={output}
                                                onChange={(selected) =>
                                                    handleTransportModeChange(index, selected)
                                                }
                                            />
                                        )
                                    })()
                                ) : isCountryCode ? (
                                    <CountryPicker
                                        data-testid={'mapping-country-picker'}
                                        value={output as Country3Codes}
                                        onChange={(e) =>
                                            handleCountryCodeChange(index, e as Country3Codes)
                                        }
                                    />
                                ) : (
                                    <LocalSelect
                                        items={outputValues.map((opt, index) => ({
                                            label: formatOutput(opt),
                                            value: index,
                                        }))}
                                        value={outputValues.indexOf(output)}
                                        onChange={(e) => handleChange(index, e)}
                                        autoFill={{
                                            state: localAutoFillMappings[index],
                                            userAction: () =>
                                                setLocalAutoFillMappings((prev) => {
                                                    const newMappings = [...prev]
                                                    newMappings[index] = false
                                                    return newMappings
                                                }),
                                        }}
                                    />
                                )}
                            </Box>
                        </Box>
                    ))}

                    <Box
                        key={'add'}
                        sx={{
                            display: 'flex',
                            width: '100%',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexDirection: 'row',
                        }}
                    >
                        <Box sx={{ width: '45%' }}>
                            <Field
                                onClick={async () => {
                                    showAddMapValueModal({ handleSelection: handleAddNewMapping })
                                }}
                                label={`Add mapping value`}
                                leftButtonIcon={<AddIcon />}
                                onLeftButtonClick={(e) => {
                                    e.preventDefault()
                                }}
                            />
                        </Box>
                    </Box>
                </Box>
            </Modal>
        </LuneThemeProvider>
    )
}

export default MapValuesModal

export const showMapValuesModal = <T,>({
    label,
    outputValues,
    formatOutput,
    mappings,
    isTradelane,
    isTransportMode,
    isCountryCode,
    autoFill,
}: MapValuesProps<T>): Promise<[string, T][] | undefined> => {
    return new Promise((resolve) => {
        const container = document.createElement('div')
        document.body.appendChild(container)
        const root = ReactDOM.createRoot(container)

        const handleClose = (selection?: [string, T][]) => {
            resolve(selection)
            setTimeout(() => {
                root.unmount()
                container.remove()
            }, 0)
        }

        root.render(
            <MapValuesModal
                isTradelane={isTradelane}
                isTransportMode={isTransportMode}
                isCountryCode={isCountryCode}
                outputValues={outputValues}
                formatOutput={formatOutput}
                mappings={mappings}
                label={label}
                modalOpen={true}
                onModalClose={handleClose}
                autoFill={autoFill}
            />,
        )
    })
}
